import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, ClipboardList } from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import { getAdminLaborCases } from '../../../features/labor-intake/laborIntakeAdminService';

const Stat = ({ label, value }) => (
  <div className="lo-surface rounded-[18px] p-6">
    <p className="text-sm font-bold text-[#806f58]">{label}</p>
    <p className="mt-3 text-[32px] font-bold text-[#293a50]">{value}</p>
  </div>
);

const LaborReportsPage = () => {
  const { user, token } = useAdminAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAdminLaborCases(user, token)
      .then(setCases)
      .finally(() => setLoading(false));
  }, [token, user]);

  const report = useMemo(() => {
    const byStatus = (statuses) => cases.filter((item) => statuses.includes(item.adminData.status)).length;
    const claimCounts = new Map();
    cases.forEach((item) => {
      (item.compensation?.claims || []).forEach((claim) => claimCounts.set(claim, (claimCounts.get(claim) || 0) + 1));
    });
    return {
      total: cases.length,
      newCount: byStatus(['new']),
      missingCount: byStatus(['missing_required', 'waiting_documents']),
      completeCount: byStatus(['complete', 'documents_completed', 'preliminarily_accepted']),
      underReview: byStatus(['under_review']),
      claims: Array.from(claimCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8),
    };
  }, [cases]);

  return (
    <LaborOperationsLayout>
      <main className="px-6 pb-12 pt-8 sm:px-10" dir="rtl">
        <header className="mb-8">
          <p className="lo-kicker font-bold">REPORTS</p>
          <h1 className="legal-title mt-3 text-[34px] font-bold text-[#293a50]">التقارير</h1>
          <p className="mt-3 text-[15px] leading-7 text-[#697483]">مؤشرات تشغيلية أولية لطلبات القضايا العمالية، بدون أي تقييم لنجاح القضية.</p>
        </header>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <Stat label="إجمالي الطلبات" value={loading ? '...' : report.total} />
          <Stat label="طلبات جديدة" value={loading ? '...' : report.newCount} />
          <Stat label="تحت المراجعة" value={loading ? '...' : report.underReview} />
          <Stat label="طلبات ناقصة" value={loading ? '...' : report.missingCount} />
          <Stat label="طلبات مكتملة" value={loading ? '...' : report.completeCount} />
        </section>

        <section className="lo-surface mt-6 overflow-hidden rounded-[18px]">
          <div className="flex items-center justify-between border-b border-[#e7e2d9] px-6 py-5">
            <h2 className="text-[18px] font-bold text-[#293a50]">أكثر أنواع المطالبات تكراراً</h2>
            <BarChart3 className="h-5 w-5 text-[#948066]" />
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {report.claims.map(([claim, count]) => (
                <div key={claim} className="flex items-center justify-between rounded-2xl bg-[#f7f5f0] px-5 py-4">
                  <span className="font-bold text-[#293a50]">{claim}</span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-bold text-[#806f58]">
                    <ClipboardList className="h-4 w-4" /> {count}
                  </span>
                </div>
              ))}
              {!loading && !report.claims.length ? <p className="py-8 text-center text-sm text-[#697483]">لا توجد مطالبات مسجلة بعد.</p> : null}
            </div>
          </div>
        </section>
      </main>
    </LaborOperationsLayout>
  );
};

export default LaborReportsPage;
